import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import { BehaviorSubject, Observable } from 'rxjs';
import { BlowerDTO, CombinedState, FormValues, PayloadDto } from '../dto/blower-payload.dto';
import { SelectedValue } from '../dto/blower-selection.dto';
import { ModelDTO } from '../models/blower-model.model';
import { Field } from '../models/field.model';
import { Product } from '../models/product.model';
import { PayloadSensorValues } from '../models/transform-form-data';
import { ProductRepository } from '../repositories/products.repository';
import { SharedStateService } from './shared-state.service';

// small helper to reduce repeated BehaviorSubject + asObservable boilerplate
function createBehavior<T>(initial: T) {
  const subject = new BehaviorSubject<T>(initial);
  return { subject, obs: subject.asObservable() };
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private productSource = new BehaviorSubject<string>('Blowers');
  currentProduct$ = this.productSource.asObservable();

  private selectedBlowerGroup = new BehaviorSubject<string>('');
  // create behavior subjects via helper to reduce repeated boilerplate
  private _sidenavPair = createBehavior<boolean>(true);
  sidenavState: BehaviorSubject<boolean> = this._sidenavPair.subject;

  private _formStatePair = createBehavior<CombinedState | null>(null);
  private formStateSubject = this._formStatePair.subject;
  formState$Model = this._formStatePair.obs;

  private _formStateConditionPair = createBehavior<CombinedState | null>(null);
  private formStateConditionSubject = this._formStateConditionPair.subject;
  formState$Condition = this._formStateConditionPair.obs;

  private _fetchedDataPair = createBehavior<ModelDTO[] | null>(null);
  private fetchedDataSubject = this._fetchedDataPair.subject;
  fetchedData$ = this._fetchedDataPair.obs;

  private _modelDataPair = createBehavior<ModelDTO[] | null>(null);
  private modelDataSubject = this._modelDataPair.subject;
  modelData$ = this._modelDataPair.obs;

  private _sensorValuesPair = createBehavior<PayloadSensorValues | null>(null);
  private sensorValuesSubject = this._sensorValuesPair.subject;
  sensorValues$ = this._sensorValuesPair.obs;

  private _selectedValuesPair = createBehavior<SelectedValue[]>([]);
  private selectedValuesSubject = this._selectedValuesPair.subject;
  selectedValues$ = this._selectedValuesPair.obs;

  private _updatedGrandParentPair = createBehavior<string[]>([]);
  private updatedGrandParentSubject = this._updatedGrandParentPair.subject;
  grandParentSelectedValue$ = this._updatedGrandParentPair.obs;

  private _filteredSelectedRowsPair = createBehavior<any[]>([]);
  private filteredSelectedRowsSubject = this._filteredSelectedRowsPair.subject;
  filteredSelectedRows$ = this._filteredSelectedRowsPair.obs;

  state: any;
  private _loadingPair = createBehavior<boolean>(false);
  private loadingSubject = this._loadingPair.subject;
  loading$ = this._loadingPair.obs;

  private _checkboxesLoadedPair = createBehavior<boolean>(false);
  private checkboxesLoadedSubject = this._checkboxesLoadedPair.subject;
  checkboxesLoaded$ = this._checkboxesLoadedPair.obs;

  private _formFieldValuesPair = createBehavior<{ [key: string]: FormValues }>({});
  private formFieldValuesSubject = this._formFieldValuesPair.subject;
  formFieldValues$ = this._formFieldValuesPair.obs;

  private _selectedCheckboxesPair = createBehavior<any[]>([]);
  private selectedCheckboxesSubject = this._selectedCheckboxesPair.subject;
  selectedCheckboxes$ = this._selectedCheckboxesPair.obs;

  private _activeTabPair = createBehavior<string>('');
  private activeTabBehaviorSubject = this._activeTabPair.subject;
  activeTab$ = this._activeTabPair.obs;

  // Store the latest form field definitions so other tabs (e.g. Graph) can reuse them
  private _formFieldsPair = createBehavior<Field[]>([]);
  private formFieldsSubject = this._formFieldsPair.subject;
  formFields$ = this._formFieldsPair.obs;

  // Cache of known model names so other tabs can read them immediately
  private _modelNamesPair = createBehavior<string[]>([]);
  private modelNamesSubject = this._modelNamesPair.subject;
  modelNames$ = this._modelNamesPair.obs;

  // Add separate storage for model form values
  private _formFieldValuesModelPair = createBehavior<{ [key: string]: FormValues }>({});
  private formFieldValuesModelSubject = this._formFieldValuesModelPair.subject;
  formFieldValuesModel$ = this._formFieldValuesModelPair.obs;

  constructor(
    private productRepository: ProductRepository,
    private sharedStateService: SharedStateService,
  ) {}

  // Generic helpers to reduce repeated BehaviorSubject boilerplate
  private setSubject<T>(subject: BehaviorSubject<T>, value: T): void {
    subject.next(value);
  }

  private getSubjectValue<T>(subject: BehaviorSubject<T>): T {
    return subject.getValue();
  }

  private mergeAndSetSubject<T extends object>(subject: BehaviorSubject<T>, value: Partial<T>): void {
    const current = subject.getValue() as any;
    subject.next({ ...(current || {}), ...(value || {}) } as T);
  }

  private clearSubject<T>(subject: BehaviorSubject<T>, emptyValue: T): void {
    subject.next(emptyValue);
  }

  setFilteredSelectedRows(rows: any[]): void {
    this.setSubject(this.filteredSelectedRowsSubject, rows);
  }
  setLoading(loading: boolean) {
    this.setSubject(this.loadingSubject, loading);
  }
  setCheckboxesLoaded(loaded: boolean) {
    this.setSubject(this.checkboxesLoadedSubject, loaded);
  }
  setFormStateModel(state: CombinedState) {
    this.setSubject(this.formStateSubject, state);
  }
  setSelectedBlowerGroup(blowerGroup: string) {
    this.setSubject(this.selectedBlowerGroup, blowerGroup);
  }

  toggleSidenav() {
    this.setSubject(this.sidenavState, !this.getSubjectValue(this.sidenavState));
  }

  getSelectedBlowerGroup(): Observable<string> {
    return this.selectedBlowerGroup.asObservable();
  }

  updateSensorValues(data: PayloadSensorValues | null) {
    this.setSubject(this.sensorValuesSubject, data);
  }
  setFormStateCondition(state: CombinedState) {
    this.setSubject(this.formStateConditionSubject, state);
  }

  getFormStateModel() {
    return this.formStateSubject.getValue();
  }

  getFormStateCondition() {
    return this.formStateConditionSubject.getValue();
  }

  updateSelectedValues(values: SelectedValue[]): void {
    this.setSubject(this.selectedValuesSubject, values);
  }

  updateSelectedGrandParentValues(values: string[]): void {
    this.setSubject(this.updatedGrandParentSubject, values);
  }
  updateFetchedData(data: ModelDTO[] | null) {
    this.setSubject(this.fetchedDataSubject, data);
  }

  updateModelData(data: ModelDTO[] | null) {
    this.setSubject(this.modelDataSubject, data);
  }
  changeProduct(product: string) {
    this.setSubject(this.productSource, product);
  }

  getByProducts(): Observable<Product[]> {
    return this.productRepository.getProducts();
  }

  getProductFields(payload: BlowerDTO[]): Observable<Map<string, object>> {
    return this.productRepository.fetchFields(payload);
  }

  getBlowerTypes(): Observable<string> {
    return this.productRepository.getBlowerTypes();
  }

  getAllModels(): Observable<string[]> {
    return this.productRepository.getAllModels();
  }

  getModelNames(): Observable<string[]> {
    return this.productRepository.getAllModels();
  }

  // Return cached model names synchronously (useful to avoid re-fetching
  // on tab switches when another component already fetched them).
  getModelNamesCached(): string[] {
    return this.modelNamesSubject.getValue();
  }

  // Allow components to publish a cached list of model names (useful to avoid
  // a small delay when switching tabs if another component already fetched them)
  updateModelNames(names: string[] | null): void {
    this.setSubject(this.modelNamesSubject, names || ([] as string[]));
  }

  getExactModels(params: PayloadDto /* selectedUnits?: any */): Observable<ModelDTO[]> {
    // NOTE: Do NOT attach selectedUnits to the outgoing payload anymore.
    // The backend should only receive numeric values and a top-level unitType when needed.
    const payload: any = { ...params };
    return this.productRepository.fetchExactModels(payload);
  }

  getSpecificModel(body: FormValues, specificModel: string /*, selectedUnits?: any */): Observable<ModelDTO[]> {
    // Do NOT include selectedUnits in requests to the backend.
    const payloadWithHeatRecovery: any = {
      ...body,
      heat_recovery_used: this.sharedStateService.getHeatRecoveryUsed(),
    };
    return this.productRepository.fetchSpecificModel(payloadWithHeatRecovery, specificModel);
  }

  // Allow components to update/read the latest form field definitions
  updateFormFields(fields: Field[] | null): void {
    this.setSubject(this.formFieldsSubject, fields || ([] as Field[]));
  }

  getFormFields(): Field[] {
    return this.formFieldsSubject.getValue();
  }

  getSuggestedModels(exactModel: any): Observable<Map<string, object>> {
    // Delegate to repository implementation (unit tests provide repository stubs)
    return this.productRepository.fetchSuggestedModels(exactModel);
  }

  // Update the setSelectedCheckbox method
  setSelectedCheckbox(data: any[]) {
    this.state = data;
    this.setSubject(this.selectedCheckboxesSubject, data);
  }
  getSelectedRows(): any[] {
    return this.state || [];
  }
  clearState(): void {
    this.state = null;
  }

  getDownloadPdf(modelName: string, isHeaterRequired: boolean, fetchSensorValues: any) {
    // Call repository and save the returned blob using FileSaver
    this.productRepository.getDownloadPdf(modelName, isHeaterRequired, fetchSensorValues).subscribe((res: any) => {
      try {
        if (res && res.body) {
          const filename = `${modelName}.pdf`;
          FileSaver.saveAs(res.body, filename);
        }
      } catch (e) {
        console.error('Failed to save PDF', e);
      }
    });
  }
  // Add method to update form field values
  updateFormFieldValues(values: { [key: string]: FormValues }): void {
    this.mergeAndSetSubject(this.formFieldValuesSubject, values);
  }

  // Add method to get stored form field values
  getFormFieldValues(): { [key: string]: FormValues } {
    return this.formFieldValuesSubject.getValue();
  }

  // Add method to clear form field values
  clearFormFieldValues(): void {
    this.clearSubject(this.formFieldValuesSubject, {});
  }

  clearSelectedCheckboxes(): void {
    this.clearSubject(this.selectedCheckboxesSubject, []);
    this.setFilteredSelectedRows([]);
    this.state = null;
  }

  setActiveTab(tab: string): void {
    this.setSubject(this.activeTabBehaviorSubject, tab);
  }

  // Add method to update model form field values
  updateFormFieldValuesModel(values: { [key: string]: FormValues }): void {
    this.mergeAndSetSubject(this.formFieldValuesModelSubject, values);
  }

  // Add method to get stored model form field values
  getFormFieldValuesModel(): { [key: string]: FormValues } {
    return this.formFieldValuesModelSubject.getValue();
  }

  // Add method to clear model form field values
  clearFormFieldValuesModel(): void {
    this.clearSubject(this.formFieldValuesModelSubject, {});
  }

  // Add method to initialize model form values from condition form values
  initializeModelFormValues(): void {
    const conditionValues = this.getSubjectValue(this.formFieldValuesSubject);
    const modelValues = this.getSubjectValue(this.formFieldValuesModelSubject);

    if (Object.keys(conditionValues || {}).length > 0 && Object.keys(modelValues || {}).length === 0) {
      this.setSubject(this.formFieldValuesModelSubject, { ...(conditionValues as any) });
    }
  }

  // Add method to initialize condition form values from model form values
  initializeConditionFormValues(): void {
    const modelValues = this.getSubjectValue(this.formFieldValuesModelSubject);
    const conditionValues = this.getSubjectValue(this.formFieldValuesSubject);

    if (Object.keys(modelValues || {}).length > 0 && Object.keys(conditionValues || {}).length === 0) {
      this.setSubject(this.formFieldValuesSubject, { ...(modelValues as any) });
    }
  }
}
